import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import Select from "react-select";
import { getSailors } from "../../../api/sailor";
import { getBoatsBySailor } from "../../../api/boat";
import { getGpsDevices } from "../../../api/gpsdevice"; // ✅ Nuevo import

export default function SeleccionarSailorDialog({
  open,
  onClose,
  onSelect,
  existingParticipants = [],
  initialValues = null,
  mode = "create",
  reservedGpsIds = [],
}) {
  const [sailors, setSailors] = useState([]);
  const [boats, setBoats] = useState([]);
  const [gpsDevices, setGpsDevices] = useState([]);

  const [selectedSailor, setSelectedSailor] = useState(null);
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [selectedGpsDevice, setSelectedGpsDevice] = useState(null);

  const ensureOption = (arr, id, labelFallback) => {
    if (!id) return null;
    const found = arr.find(o => o.value === id);
    return found || (labelFallback ? { value: id, label: labelFallback } : null);
  };

  useEffect(() => {
    if (open) {
      // reset selección (o precarga si edit)
      if (mode === 'edit' && initialValues) {
        setSelectedSailor(initialValues.sailor ? { label: initialValues.sailor.fullName, value: initialValues.sailor._id } : null);
        setSelectedBoat(initialValues.boat ? { label: initialValues.boat.fullName, value: initialValues.boat._id } : null);
        setSelectedGpsDevice(initialValues.gpsDevice ? { label: initialValues.gpsDevice.code, value: initialValues.gpsDevice._id } : null);
      } else {
        setSelectedSailor(null);
        setSelectedBoat(null);
        setSelectedGpsDevice(null);
      }

      getSailors().then((res) => {
        if (res.status === 200) {
          const editingSailorId = initialValues?.sailor?._id;
          const registeredIds = existingParticipants.map((p) => p.sailor?._id).filter(Boolean);
          const allow = new Set(registeredIds);
          // Permite al sailor ya asignado cuando editas
          if (mode === 'edit' && editingSailorId) allow.delete(editingSailorId);

          const filtered = res.data.filter((s) => !allow.has(s._id));
          setSailors(filtered.map((s) => ({ label: s.fullName, value: s._id })));
        }
      });

      getGpsDevices().then((res) => {
        if (res.status === 200) {
          const used = new Set(reservedGpsIds); // ocupados en esta regata
          // Si estás editando, permite el GPS actual del participante
          const currentGpsId = initialValues?.gpsDevice?._id;
          if (currentGpsId) used.delete(currentGpsId);

          setGpsDevices(
            res.data.map((d) => ({
              label:
                d.code +
                (d.lastLatitude ? ` (${d.lastLatitude.toFixed(4)}, ${d.lastLongitude?.toFixed(4)})` : '') +
                (used.has(d._id) ? ' — ocupado' : ''),
              value: d._id,
              isDisabled: used.has(d._id),
            }))
          );
        }
      });
    }
  }, [open, existingParticipants, mode, initialValues, reservedGpsIds]);


  useEffect(() => {
    if (!selectedSailor) {
      setBoats([]);
      setSelectedBoat(null);
      return;
    }
    getBoatsBySailor(selectedSailor.value).then((res) => {
      if (res.status === 200) {
        const options = res.data.map((b) => ({ label: b.fullName, value: b._id }));
        setBoats(options);

        // Si estamos en edición y el boat inicial pertenece al sailor actual, precargarlo
        const currentBoatId = initialValues?.boat?._id || initialValues?.boat || null;
        if (currentBoatId) {
          const opt = ensureOption(options, currentBoatId, initialValues?.boat?.fullName);
          if (opt) setSelectedBoat(opt);
        }
      } else {
        setBoats([]);
        setSelectedBoat(null);
      }
    });
  }, [selectedSailor, initialValues]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEnforceFocus disableRestoreFocus>
      <DialogTitle>{mode === "edit" ? "Editar participante" : "Seleccionar Sailor, Boat y GPS"}</DialogTitle>
      <DialogContent>
        <div className="mb-3">
          <label>Sailor</label>
          <Select
            options={sailors}
            value={selectedSailor}
            onChange={(opt) => {
              setSelectedSailor(opt);
              setSelectedBoat(null);
            }}
            placeholder="Selecciona un sailor"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 1300 }) }}
            required
          />
        </div>
        {selectedSailor && (
          <div className="mb-3">
            <label>Boat</label>
            <Select
              options={boats}
              value={selectedBoat}
              onChange={setSelectedBoat}
              placeholder="Selecciona un boat"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 1300 }) }}
              required
            />
          </div>
        )}
        <div className="mb-3">
          <label>GPS Device</label>
          <Select
            options={gpsDevices}
            value={gpsDevices.find((g) => g.value === selectedGpsDevice?.value) || null}
            onChange={setSelectedGpsDevice}
            placeholder="Selecciona un GPS"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 1300 }) }}
            isOptionDisabled={(opt) => !!opt.isDisabled}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={() => {
            const chosen = selectedGpsDevice?.value;
            const current = initialValues?.gpsDevice?._id;
            const isBlocked = reservedGpsIds.includes(chosen) && chosen !== current;
            if (!selectedBoat || !selectedGpsDevice) {
              alert("Debes seleccionar un boat y un GPS");
              return;
            }
            if (isBlocked) {
              alert("Ese GPS ya está asignado a otro participante de esta regata.");
              return;
            }
            onSelect({
              sailor: selectedSailor?.value || null,
              boat: selectedBoat?.value || null,
              gpsDevice: chosen || null,
            });
            onClose();
          }}
          className="btn btn-primary"
          variant="contained"
          disabled={!selectedSailor}
        >
          {mode === "edit" ? "Guardar" : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
